<template>
  <div class="page-add-invoice mt-5">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li><router-link to="/dashboard">Dashboard</router-link></li>
        <li><router-link to="/dashboard/invoices">Invoices</router-link></li>
        <li class="is-active"><router-link to="/dashboard/invoices/create" aria-current="true">Add</router-link></li>
      </ul>
    </nav>

    <div class="form-container">
      <div class="column is-12">
        <h1 class="title">{{ isEditMode ? 'Edit Invoice' : 'Add Invoice' }}</h1>
      </div>

      <!-- Document Selector Card -->
      <div class="card">
        <header class="card-header">
          <p class="card-header-title">Document Properties</p>
        </header>
        <div class="card-content">
          <div class="columns">
            <div class="column is-one-third">
              <div class="pl-5 mt-2">
                <label class="switch">
                  <input type="checkbox" v-model="isSelfBilling" @change="toggleSelfBilling" :disabled="isEditMode">
                  <span class="check"></span>
                  <span class="control-label">Self Billing</span>
                </label>
              </div>
            </div>
            <div class="column is-one-third">
              <p v-if="v$.invoice.invoice_type.$error" class="pl-5 help is-danger">
                <span>Please select one</span>
              </p>
              <div class="select pl-5 is-small" :class="{ 'is-danger': v$.invoice.invoice_type.$error }">
                <select
                  v-model="invoice.invoice_type"
                  @change="handleInvoiceTypeChange"
                  :disabled="isEditMode">
                  <option value="">- Select Invoice Type -</option>
                  <option v-if="!isSelfBilling" value="01">01 - Invoice</option>
                  <option v-if="!isSelfBilling" value="02">02 - Credit Note</option>
                  <option v-if="!isSelfBilling" value="03">03 - Debit Note</option>
                  <option v-if="!isSelfBilling" value="04">04 - Refund Note</option>
                  <option v-if="isSelfBilling" value="11">11 - SelfBilling Invoice</option>
                  <option v-if="isSelfBilling" value="12">12 - SelfBilling Credit Note</option>
                  <option v-if="isSelfBilling" value="13">13 - SelfBilling Debit Note</option>
                  <option v-if="isSelfBilling" value="14">14 - SelfBilling Refund Note</option>
                </select>
              </div>
            </div>
            <div class="field is-size-7 column is-one-third" :class="{ 'has-text-danger': v$.invoice.sender_store_code.$error }">
              <div class="select is-small" :class="{ 'is-danger': v$.invoice.sender_store_code.$error }">
                <select v-model="invoice.sender_store_code" :disabled="isEditMode">
                  <option value="">- Select Department Code -</option>
                  <option v-for="store in userProfile.store_code" :key="store.id" :value="store.code">
                    {{ store.code }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isEditMode || documentSelected">
        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Company Selector</p>
          </header>
          <div class="card-content">
            <div class="columns pl-4">
              <div class="column is-half" :class="{ 'is-danger': v$.invoice.sender.$error }">
                <h2 class="is-size-6 mb-1" :class="{ 'has-text-danger': v$.invoice.sender.$error }">Supplier</h2>
                <div :class="{ 'is-danger': v$.invoice.sender.$error }">
                  <multiselect
                    v-model="invoice.sender"
                    :options="senders"
                    label="name"
                    track-by="entity_code"
                    placeholder="- Type At Least 3 Characters -"
                    :searchable="true"
                    :close-on-select="true"
                    :clear-on-select="false"
                    :allow-empty="false"
                    @search-change="getClients"
                    :custom-label="formatSender"
                    :disabled="isEditMode"
                                    >
                    <template #noResult>
                      <span v-if="showCustomerHint">Type at least 3 characters to search</span>
                      <span v-else>No matching results found</span>
                    </template>
                  </multiselect>
                </div>
                
                <p v-if="v$.invoice.sender.$error" class="pl-5 help is-danger">
                  <span>Please select Sender</span>
                </p>
                <div class="box mt-4 is-size-7" v-if="invoice.sender">
                  <p><strong>{{ invoice.sender.name }}</strong></p>
                  <p><strong>TIN No:</strong> {{ invoice.sender.tin_no }}</p>
                  <p><strong>Identity No ({{ invoice.sender.identity_type }}):</strong> {{ invoice.sender.identity_number }}</p>
                  <p><strong>Email:</strong> {{ invoice.sender.email }}</p>
                  <p><strong>Phone:</strong> {{ invoice.sender.contact_number }}</p>
                  <p v-if="invoice.sender.address1">{{ invoice.sender.address1 }}</p>
                  <p v-if="invoice.sender.address2">{{ invoice.sender.address2 }}</p>
                  <p v-if="invoice.sender.zipcode || invoice.sender.city">{{ invoice.sender.zipcode }} {{ invoice.sender.city }}</p>
                  <p v-if="invoice.sender.country">{{ invoice.sender.country }}</p>
                </div>
              </div>

              <div class="column is-half" :class="{ 'is-danger': v$.selectedCustomer.$error }">
                <h2 class="is-size-6 mb-1" :class="{ 'has-text-danger': v$.selectedCustomer.$error }">Buyer</h2>
                <div :class="{ 'is-danger': v$.selectedCustomer.$error }">
                  <multiselect
                    v-model="selectedCustomer"
                    :options="customers"
                    label="name"
                    track-by="entity_code"
                    placeholder="- Type At Least 3 Characters -"
                    :searchable="true"
                    :close-on-select="true"
                    :clear-on-select="false"
                    :allow-empty="false"
                    @search-change="getClients"
                    :custom-label="formatCustomer"
                    :disabled="isEditMode"
                                    >
                    <template #noResult>
                      <span v-if="showCustomerHint">Type at least 3 characters to search</span>
                      <span v-else>No matching results found</span>
                    </template>
                  </multiselect>
                </div>
                <!-- <p v-if="v$.invoice.customer.$error" class="pl-5 help is-danger">
                    <span>Please select Receiver</span>
                </p> -->
                <div class="box mt-4 is-size-7" v-if="selectedCustomer">
                  <p><strong>{{ selectedCustomer.name }}</strong></p>
                  <p><strong>TIN No:</strong> {{ selectedCustomer.tin_no }}</p>
                  <p><strong>Identity No ({{ selectedCustomer.identity_type }}):</strong> {{ selectedCustomer.identity_number }}</p>
                  <p><strong>Email:</strong> {{ selectedCustomer.email }}</p>
                  <p><strong>Phone:</strong> {{ selectedCustomer.contact_number }}</p>
                  <p v-if="selectedCustomer.address1">{{ selectedCustomer.address1 }}</p>
                  <p v-if="selectedCustomer.address2">{{ selectedCustomer.address2 }}</p>
                  <p v-if="selectedCustomer.zipcode || selectedCustomer.city">{{ selectedCustomer.zipcode }} {{ selectedCustomer.city }}</p>
                  <p v-if="selectedCustomer.country">{{ selectedCustomer.country }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Document Header</p>
          </header>
          <div class="columns card-content">
            <div class="column is-6">
              <div class="column is-8">
                <div class="field is-size-7">
                  <label>Invoice Number</label>
                  <div class="control">
                    <input type="text" class="input is-small" v-model="invoice.invoice_number" disabled>
                  </div>
                </div>
                <div class="field is-size-7">
                  <label>Invoice Date</label>
                  <div class="control">
                    <input type="date" class="input is-small" v-model="invoice.invoice_date" disabled>
                  </div>
                </div>
                
                <div class="field is-size-7">
                  <label>Currency</label>
                  <div class="control">
                    <div class="select is-small">
                      <select v-model="invoice.invoice_currency" @change="handleCurrencyChange">
                        <option v-for="currency in currencies" :key="currency.code" :value="currency.code">
                          {{ currency.code }} - {{ currency.name }}
                        </option>
                      </select>
                    </div>
                    <p v-if="v$.invoice.invoice_currency.$error" class="pl-5 help is-danger">
                        <span>Please select Currency</span>
                    </p>
                  </div>
                </div>

                <div v-if="invoice.invoice_currency !== 'MYR'" class="field is-size-7">
                  <label>Currency Exchange Rate *</label>
                  <div class="control">
                    <input type="number" step="0.0001" class="input is-small" v-model="invoice.currency_exchange_rate" required>
                  </div>
                </div>

                <div class="field is-size-7">
                  <label>Agent Code</label>
                  <div class="control">
                    <input type="text" class="input is-small" v-model="invoice.agent_number">
                  </div>
                </div>

                <div class="field is-size-7">
                  <label>Contract Number</label>
                  <div class="control">
                    <input type="text" class="input is-small" v-model="invoice.contract_number">
                  </div>
                </div>


              </div>
            </div>

            <div class="column is-6">
              <div class="column is-8">
                <div class="field is-size-7">
                  <label>Billing Start Date</label>
                  <div class="control">
                    <input type="date" class="input is-small" v-model="invoice.billing_start">
                  </div>
                </div>

                <div class="field is-size-7">
                  <label>Billing End Date</label>
                  <div class="control">
                    <input type="date" class="input is-small" v-model="invoice.billing_end">
                  </div>
                </div>

                <div class="field is-size-7">
                  <label>Frequency of Billing</label>
                    <div class="control">
                      <input type="text" class="input is-small" v-model="invoice.frequency_of_billing" required>
                    </div>
                </div>

                <div class="columns field is-size-7 ">
                  <div class="column">
                    <label>Rounding Amount</label>
                    <div class="control" @change="updateTotals">
                      <input type="number" step="0.01" class="input is-small has-text-right" v-model="invoice.rounding_amount">
                    </div>
                  </div>
                  <div class="column">
                    <label>Pre-payment Amount</label>
                    <div class="control" @change="updateTotals">
                      <input type="number" step="0.01" class="input is-small has-text-right" v-model="invoice.prepayment_amount">
                    </div>
                  </div>
                </div>
                <div v-if="invoice.invoice_type !== '01' && invoice.invoice_type !== '11' && invoice.invoice_type !== ''">
                  <div class="field is-size-7">
                    <label>Original LHDN Invoice UUID *</label>
                    <div class="control">
                      <input type="text" class="input is-small" v-model="invoice.original_inv_ref_number" required>
                    </div>
                  </div>
                  <div class="field is-size-7">
                    <label>Original Seller Invoice No *</label>
                    <div class="control">
                      <input type="text" class="input is-small" v-model="invoice.original_inv_number" required>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card">
          <header class="card-header">
            <p class="card-header-title" :class="{ 'has-text-danger': !isItemSelected }">Items</p>
          </header>
          <div class="card-content">
            <ItemForm
              v-for="(item, index) in invoice.items"
              :key="item.id"
              :initialItem="item"
              @updateItem="updateItem($event, index)"
              @delete-item="deleteItem"
              :classification-options="classificationOptions"
              :description-options="descriptionOptions"
            ></ItemForm>
            <button class="field button is-small is-info" @click="addItem">+ Add Item</button>
          </div>
        </div>

        <div class="card">
          <header class="card-header">
            <p class="card-header-title">Document Summary</p>
          </header>
          <div class="card-content">
            <div class="columns">
              <div class="column is-half">
                <div class="field column is-10">
                  <label>Additional Comments</label>
                    <div class="control is-small">
                      <input type="text" class="input is-small" v-model="invoice.document_remarks">
                    </div>
                </div>
                <div class="field column is-5">
                  <label>Payment Due Date</label>
                  <div class="control">
                    <input type="date" class="input is-small" v-model="invoice.payment_due_date">
                  </div>
                </div>
                <div v-if="invoice.tax_rate == 0" class="field column is-6">
                  <div class="column is-size-3">
                    <div class=" is-size-6">
                      <label>Tax Exemption Reason</label>
                      <div class="control is-small">
                        <input type="text" class="input is-small" v-model="invoice.tax_exemption_reason">
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="column is-12">
                <h2 class="is-size-5 mb-4">Invoice Summary</h2>
                <p><strong>Gross amount</strong>: {{ formatCurrency(invoice.total_excluding_tax, invoice.invoice_currency) }}</p>
                <p><strong>Discount amount</strong>: {{ formatCurrency(invoice.total_discount_value, invoice.invoice_currency) }}</p>
                <p><strong>Tax amount</strong>: {{ formatCurrency(invoice.total_tax_amount, invoice.invoice_currency) }}</p>
                <p v-if="invoice.prepayment_amount !== '0.00'">
                  <strong>Pre-payment Amount</strong>: {{ formatCurrency(invoice.prepayment_amount, invoice.invoice_currency) }}
                </p>
                <p v-if="invoice.rounding_amount !== '0.00'">
                  <strong>Rounding Amount</strong>: {{ formatCurrency(invoice.rounding_amount, invoice.invoice_currency) }}
                </p>
                <p><strong>Payable amount</strong>: {{ formatCurrency(invoice.total_payable_amount, invoice.invoice_currency) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isEditMode || documentSelected">
      <div class="buttons mt-6 ml-6 pl-6">
        <button class="button is-primary" @click="previewInvoice">Preview</button>
        <button class="button is-success ml-3" @click="handleInvoice('submit')">Request Approval</button>
        <button class="button is-info ml-3" @click="showSaveTemplateModal = true">Save as Template</button>
      </div>
    </div>

    <!-- Loader Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        <p class="loading-text">Submitting your invoice...</p>
        <Vue3Lottie
          animationLink="https://assets9.lottiefiles.com/packages/lf20_kdx6cani.json"
          style="width: 150px; height: 150px;"
          :loop="true"
          :autoplay="true"
        />
      </div>
    </div>

    <TemplateModal
      :isVisible="showSaveTemplateModal"
      @close="showSaveTemplateModal = false"
      @save-template="saveTemplate"
    />

    <!-- response modal -->
    <ResponseModal
      v-if="showResponseModal"
      :serverResponse="serverResponse"
      :isErrorResponse="isErrorResponse"
      @close="handleModalClose"
    />
  </div>

  <InvoicePreview :showPreview="showPreview" :logo="previewLogo" :sender="invoice.sender" :invoice="invoice" :customer="selectedCustomer" @close="showPreview = false" />
</template>

<script>
import axios from 'axios';
import { toast } from 'bulma-toast';
import ItemForm from '@/components/CreateInvoice/ItemForm.vue';
import ResponseModal from '@/components/CreateInvoice/ResponseModal.vue';
import InvoicePreview from '@/components/CreateInvoice/InvoicePreview.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import 'bulma-switch/dist/css/bulma-switch.min.css';
import TemplateModal from '@/components/CreateInvoice/TemplateModal.vue';
import { Vue3Lottie } from 'vue3-lottie';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

export default {
  name: 'AddInvoice',
  components: {
    ItemForm,
    InvoicePreview,
    Multiselect,
    TemplateModal,
    ResponseModal,
    Vue3Lottie,
  },
    // Define validations as a top-level option
  validations: {
    invoice: {
      invoice_type: { required },
      sender: { required },
      invoice_currency: { required },
      sender_store_code: { required },
      // ... add additional validations as needed ...
    },
    selectedCustomer: {
      required
    }
  },
  data() {
    return {
      invoice: {
        sender: '',
        customer: '',
        invoice_type: '',
        selfbilling: false,
        source: 'UI',
        invoice_number: '',
        invoice_date: this.formatDate(new Date()),
        payment_due_date: '',
        invoice_time: '',
        invoice_currency: 'MYR',
        currency_exchange_rate: '', // Exchange rate field
        sender_store_code: '', // Initialize as empty
        document_type: '',
        original_inv_number: '',
        original_inv_ref_number: '',
        prepayment_amount: '0.00',
        document_remarks: '',
        items: [
          {
            id: 0,
            classification: '',
            item_description: '',
            unit_price: '0.00',
            quantity: '1.00',
            tax_rate: '0.0',
            tax_type: '06',
            subtotal: '0.00',
            total_excluding_tax: '0.00',
            tax_amount: '0.00',
            discount_rate: '0.00',
            discount_amount: '0.00',
            unit_of_measurement: 'EA',
          },
        ],
        total_excluding_tax: '0.00',
        total_including_tax: '0.00',
        total_net_amount: '0.00',
        total_discount_value: '0.00',
        total_tax_amount: '0.00',
        total_payable_amount: '0.00',
        tax_rate: '0',
        rounding_amount: '0.00',
        tax_exemption_reason: 'Non SST Registrant',
        billing_start: '',
        billing_end: '',
        frequency_of_billing: '',
        agent_number: '',
        contract_number: '',
      },
      senders: [],
      customers: [],
      selectedCustomer: '',
      selectedInvoiceType: '',
      showPreview: false,
      isSelfBilling: false,
      previewLogo: null,
      originalSenders: [],
      originalCustomers: [],
      showSaveTemplateModal: false,
      classificationOptions: [], // New data property
      descriptionOptions: [], // New data property
      currencies: [],
      userProfile: {
        sender_entities: [],
        store_code: [], // Initialized as empty
      },
      isLoading: false, 
      serverResponse: null,
      showResponseModal: false,
      isErrorResponse: false,
    };
  },
  computed: {
    documentSelected() {
      return !!this.invoice.invoice_type && !!this.invoice.sender_store_code;
    },
    isItemSelected() {
      return this.invoice.items.length > 0;
    },
    isTemplateMode() {
      return this.$route.query.isTemplate === 'true';
    },
    isEditMode() {
      return !!this.$route.params.uuid; // True if editing, false if creating
    },
    invoice_type: {
      get() {
        return this.selectedInvoiceType.substring(0, 2);
      },
      set(value) {
        this.selectedInvoiceType = value;
        this.invoice.invoice_type = value.substring(0, 2);
      }
    }
  },
  setup() {
    const v$ = useVuelidate();
    return { v$ };
  },
  async mounted() {
    await this.getUserProfile(); // Ensure user profile is fetched
    await this.fetchCurrencies();
    if (!this.isEditMode) {
      await this.getSenders();
      await this.getClients();
    }
    this.setCurrentTime();
    if (this.$route.params.uuid) {
    await this.fetchInvoice(this.$route.params.uuid);
    // Check if the invoice was loaded from a template
    if (this.$route.query.isTemplate === 'true') {
      // Generate a new invoice number
      await this.generateInvoiceNumber();
      // Set the invoice date to today's date using your formatDate helper
      this.invoice.invoice_date = this.formatDate(new Date());
    }
  }
  },
  watch: {
    // Watch for changes in userProfile.store_code
    'userProfile.store_code': function (newStoreCode) {
      if (newStoreCode.length === 1) {
        this.invoice.sender_store_code = newStoreCode[0].code;
      }
    },
    'invoice.sender_store_code'(newVal) {
      if (newVal && this.invoice.invoice_type && !this.isEditMode) {
        this.generateInvoiceNumber();
      }
    },
  },
  methods: {
    async fetchInvoice(uuid) {
      try {
        const { isTemplate } = this.$route.query;
        const endpoint = isTemplate === 'true'
          ? `/api/v1/integration/invoice-templates/${uuid}/`
          : `/api/v1/invoices/${uuid}/`;
        const response = await axios.get(endpoint);
        this.invoice = response.data; // Update the form with the fetched invoice data
        this.selectedCustomer = this.invoice.customer
      } catch (error) {
        console.error('Error fetching invoice:', error);
        this.$router.push('/dashboard/invoices'); // Redirect if invoice not found
      }
    },
    async fetchCurrencies() {
      try {
        const response = await axios.get('https://openexchangerates.org/api/currencies.json', {
          params: {
            app_id: 'YOUR_API_KEY' // Replace with your API key
          }
        });
        this.currencies = Object.entries(response.data).map(([code, name]) => ({
          code,
          name
        }));
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    },
    formatSender(sender) {
      return sender ? `${sender.name} (${sender.entity_code})` : '';
    },
    formatCustomer(customer) {
      return customer ? `${customer.name} (${customer.entity_code})` : '';
    },
    previewInvoice() {
      // Check if selfbilling is true or false
      if (this.isSelfBilling) {
        // Get the sender_entities from localStorage and parse it
        this.previewLogo = this.selectedCustomer.image;
        console.log('Preview Logo:', this.selectedCustomer);

      } else {
        // Use the sender logo from the invoice if selfbilling is false
        this.previewLogo = this.invoice.sender.image;
      }
      // Show the invoice preview modal
      this.showPreview = true;
    },
    handleCurrencyChange() {
      if (this.invoice.invoice_currency === 'MYR') {
        this.invoice.currency_exchange_rate = ''; // Clear exchange rate if MYR is selected
      }
    },
    handleModalClose() {
      this.showResponseModal = false;
      // If the response was successful, route back to the create invoice page
      if (!this.isErrorResponse) {
        this.$router.push('/dashboard/create-document');
      }
    },
    async getSenders() {
      try {
        // Assume that userProfile is already populated by getUserProfile method
        this.originalSenders = this.userProfile.sender_entities;
        if (!this.isSelfBilling) {
          this.senders = this.originalSenders;
          if (this.senders.length === 1) {
            this.invoice.sender = this.senders[0];
          }
        }
      } catch (error) {
        console.log(JSON.stringify(error));
      }
    },
    async getClients(search = '') {
      if (search.length < 3) {
        this.customers = [];
        this.senders = [];
        return;
      }

      try {
        const response = await axios.get('/api/v1/customers-list/', {
          params: { search }
        });

        this.originalCustomers = response.data;

        if (this.isSelfBilling) {
          this.senders = this.originalCustomers; // ⬅️ populate the `senders` multiselect used for self billing
        } else {
          this.customers = this.originalCustomers; // ⬅️ normal mode
        }

        // Optional: Auto-select if only one result
        if (this.isSelfBilling && this.senders.length === 1) {
          this.invoice.sender = this.senders[0];
        }
        if (!this.isSelfBilling && this.customers.length === 1) {
          this.selectedCustomer = this.customers[0];
        }

      } catch (error) {
        console.log(JSON.stringify(error));
      }
    },
    async getUserProfile() {
      try {
        // Fetch and parse user profile data from local storage or API
        const senderEntities = JSON.parse(localStorage.getItem('sender_entities')) || [];
        const storeCode = JSON.parse(localStorage.getItem('store_code')) || [];

        // Set the userProfile state
        this.userProfile.sender_entities = senderEntities;
        this.userProfile.store_code = storeCode;
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    },
    saveTemplate(templateName, templateDescription) {
      // Call the existing handleInvoice function with 'saveTemplate' as the operation.
      this.handleInvoice('saveTemplate', templateName, templateDescription);
    },
    toggleSelfBilling() {
      this.invoice.invoice_number = '';
      this.invoice.invoice_type = '';
      if (this.isSelfBilling) {
        this.invoice.tax_rate = 0;
        this.invoice.tax_exemption_reason = '';
        this.senders = this.originalCustomers;
        this.customers = this.originalSenders;
      } else {
        this.senders = this.originalSenders;
        this.customers = this.originalCustomers;
      }
      // Auto-select if there's only one sender
      if (this.senders.length === 1) {
        this.invoice.sender = this.senders[0];
      } else {
        this.invoice.sender = '';
      }

      // Auto-select if there's only one customer
      if (this.customers.length === 1) {
        this.selectedCustomer = this.customers[0];
      } else {
        this.selectedCustomer = '';
      }
 
    },
    handleInvoiceTypeChange() {
        this.generateInvoiceNumber();
    },
    async generateInvoiceNumber() {
      if (this.isEditMode && !this.isTemplateMode) return;
      try {
        const response = await axios.get('/api/v1/invoice_number', {
          params: {
            invoice_type: this.invoice.invoice_type,
            store_code: this.invoice.sender_store_code
          }
        });
        this.invoice.invoice_number = 'M' + '-' + this.invoice.sender_store_code + '-' + response.data.invoice_number;
      } catch (error) {
        console.error('Error generating invoice number:', error);
      }
    },
    async handleInvoice(operation, templateName = null, templateDescription = null) {
      if (operation !== 'saveTemplate') {
        this.v$.$touch(); // Mark all fields as touched

        // Check if at least one item exists
          if (this.invoice.items.length === 0) {
            // Optionally, update a flag that you bind to the label (e.g., this.itemsError = true)
            toast({
              message: 'Please add at least one item before submitting.',
              type: 'is-danger',
              dismissible: true,
              pauseOnHover: true,
              duration: 3000,
              position: 'bottom-right',
            });
            return;
          }


            if (this.v$.$invalid) {
          toast({
            message: 'Please correct the highlighted fields.',
            type: 'is-danger',
            dismissible: true,
            pauseOnHover: true,
            duration: 3000,
            position: 'bottom-right',
          });
          return;
        }
      }
      if (operation === 'saveTemplate' && !templateName) {
        toast({
          message: 'Please provide a name for the template.',
          type: 'is-danger',
          dismissible: true,
          pauseOnHover: true,
          duration: 3000,
          position: 'bottom-right',
        });
        return;
      }

      if (operation === 'submit' && !this.validateForm()) {
        toast({
          message: 'Please complete all mandatory fields.',
          type: 'is-danger',
          dismissible: true,
          pauseOnHover: true,
          duration: 3000,
          position: 'bottom-right',
        });
        return;
      }

      this.invoice.customer = this.selectedCustomer;

      if (this.isSelfBilling) {
        this.invoice.selfbilling = true;
        this.invoice.document_type = 'SB';
        this.invoice.sender_entity_code = operation === 'saveTemplate' ? this.invoice.sender.tin_no : this.invoice.sender.entity_code;
      }

      this.invoice.items = this.invoice.items.map((item, index) => ({
        ...item,
        id: operation === 'submit' ? this.generateUniqueId(index) : undefined,
        unit_price: parseFloat(item.unit_price).toFixed(2),
        subtotal: parseFloat(item.subtotal).toFixed(2),
        total_excluding_tax: parseFloat(item.total_excluding_tax).toFixed(2),
        tax_amount: parseFloat(item.tax_amount).toFixed(2),
        discount_amount: parseFloat(item.discount_amount).toFixed(2),
        discount_rate: parseFloat(item.discount_rate).toFixed(2),
      }));

      const formattedInvoiceData = this.formatInvoiceData();

      const requestBody =
        operation === 'submit'
          ? formattedInvoiceData // Directly use the formatted data
          : {
              name: templateName,
              data: {
                description: templateDescription,
                invoice: formattedInvoiceData,
              },
            };

      // Show the loader
      this.isLoading = true;

      try {
        const endpoint =
          operation === 'submit' ? '/api/v1/integration/submit-json/' : '/api/v1/integration/invoice-templates/';
          const response = await axios.post(endpoint, requestBody, {
            headers: {
              'source': 'UI'  // ✅ Adding source == 'UI' in the request headers
            }
          });
        if (operation === 'saveTemplate') {
          toast({
            message: 'Template saved successfully',
            type: 'is-success',
            dismissible: true,
            pauseOnHover: true,
            duration: 2000,
            position: 'bottom-right',
          });
        }

        if (operation === 'submit') {
          // Instead of immediately redirecting, set the response and show the modal
          this.serverResponse = response.data;
          this.isErrorResponse = false;
          this.showResponseModal = true;
        } else {
          this.showSaveTemplateModal = false;
          this.serverResponse = 'Template Saved Successfully';
          setTimeout(() => {
            this.$router.push('/dashboard/create-document');
          }, 1000);
        }

      } catch (error) {
        // If there's an error, capture the error response and show modal
        this.serverResponse = error.response ? error.response.data : { error: `Error ${operation === 'submit' ? 'submitting invoice' : 'saving template'}` };
        this.isErrorResponse = true;
        this.showResponseModal = true;
      } finally {
        // Hide the loader regardless of outcome
        this.isLoading = false;
      }  
    },
    validateForm() {
      return (
        this.invoice.invoice_type &&
        this.invoice.sender &&
        this.selectedCustomer &&
        this.invoice.invoice_number &&
        this.invoice.invoice_date &&
        this.invoice.invoice_currency &&
        this.invoice.sender_store_code &&
        this.invoice.items.length > 0
      );
    },
    formatCurrency(value, currencyCode = 'MYR') {
      if (!value) return `${currencyCode} 0.00`;
      return new Intl.NumberFormat('en-MY', {
        style: 'currency',
        currency: currencyCode
      }).format(value);
    },
    formatInvoiceData () {
      return [
        {
          invoices_data: [
            {
              source: "UI",
              approval_status: "Pending Approval",
              invoice_type: this.invoice.invoice_type,
              document_type: this.invoice.document_type,
              invoice_number: this.invoice.invoice_number,
              invoice_date: this.invoice.invoice_date,
              source_system_code: this.invoice.source_system_code || "DESPAQ",
              // payment_due_date: this.invoice.payment_due_date,
              invoice_time: this.invoice.invoice_time,
              sender_store_code: this.invoice.sender_store_code,
              original_inv_number: this.invoice.original_inv_number,
              original_inv_ref_number: this.invoice.original_inv_ref_number,
              document_remarks: this.invoice.document_remarks,
              prepayment_amount: parseFloat(this.invoice.prepayment_amount),
              selfbilling: this.invoice.selfbilling,
              invoice_currency: this.invoice.invoice_currency,
              currency_exchange_rate: this.invoice.currency_exchange_rate || "1.00",
              total_excluding_tax: parseFloat(this.invoice.total_excluding_tax),
              total_including_tax: parseFloat(this.invoice.total_including_tax),
              sender_entity_code: this.invoice.sender.entity_code,
              total_payable_amount: parseFloat(this.invoice.total_payable_amount),
              total_net_amount: parseFloat(this.invoice.total_net_amount),
              total_discount_value: parseFloat(this.invoice.total_discount_value),
              total_tax_amount: parseFloat(this.invoice.total_tax_amount),
              rounding_amount: parseFloat(this.invoice.rounding_amount),
              tax_rate: parseFloat(this.invoice.tax_rate),
              billing_start: this.invoice.billing_start ? this.invoice.billing_start : null,
              billing_end: this.invoice.billing_end ? this.invoice.billing_end : null,
              frequency_of_billing: this.invoice.frequency_of_billing,
              agent_number: this.invoice.agent_number,
              contract_number: this.invoice.contract_number,
              customer: { ...this.invoice.customer },
              items: this.invoice.items.map((item) => ({
                id: item.id,
                classification: item.classification || "",
                item_description: item.item_description || "",
                unit_price: parseFloat(item.unit_price),
                quantity: parseFloat(item.quantity),
                tax_rate: parseFloat(item.tax_rate),
                tax_type: item.tax_type || "",
                subtotal: parseFloat(item.subtotal),
                total_excluding_tax: parseFloat(item.total_excluding_tax),
                tax_exemption_reason: item.tax_exemption_reason,
                tax_amount: parseFloat(item.tax_amount),
                discount_rate: parseFloat(item.discount_rate || 0),
                discount_amount: parseFloat(item.discount_amount || 0),
                unit_of_measurement: item.unit_of_measurement || "",
                charges_rate: parseFloat(item.charges_rate || 0),
                product_tariff: item.product_tariff || "",
                country_origin: item.country_origin || "",
              })),
            },
          ],
        },
      ];
    },

    generateUniqueId(index) {
      const timestamp = Date.now();
      return parseInt(`${timestamp}${index}`);
    },
    addItem() {
      const index = this.invoice.items.length;
      const uniqueId = this.generateUniqueId(index);
      this.invoice.items.push({
        id: uniqueId,
        classification: '',
        item_description: '',
        unit_price: '0.00',
        quantity: '1.00',
        tax_rate: '0.00',
        tax_type: 'Not Applicable',
        subtotal: '0.00',
        total_excluding_tax: '0.00',
        tax_amount: '0.00',
        discount_rate: '0.00',
        discount_amount: '0.00',
        unit_of_measurement: 'EA'
      });
    },
    deleteItem(itemId) {
      this.invoice.items = this.invoice.items.filter(item => item.id !== itemId);
      this.updateTotals();
    },
    updateItem(updatedItem, index) {
      this.invoice.items[index] = updatedItem;
      this.updateTotals();
    },
    updateTotals() {
      let net_amount = 0;
      let tax_amount = 0;
      let disc_amount = 0;

      for (let i = 0; i < this.invoice.items.length; i++) {
        const item = this.invoice.items[i];
        const item_net_amount = parseFloat(item.unit_price) * item.quantity || 0;
        const discount = parseFloat(item.discount_amount) || 0;
        const item_tax_amount = (item_net_amount - discount) * (parseFloat(item.tax_rate) / 100 || 0);

        item.subtotal = item_net_amount.toFixed(2);
        item.total_excluding_tax = (item_net_amount - discount).toFixed(2);
        item.tax_amount = item_tax_amount.toFixed(2);
        item.discount_amount = discount.toFixed(2);

        tax_amount += item_tax_amount;
        net_amount += item_net_amount - discount; // Subtract discount for net amount
        disc_amount += discount;
      }

      const rounding_amount = parseFloat(this.invoice.rounding_amount) || 0;
      const prepayment_amount = parseFloat(this.invoice.prepayment_amount) || 0;

      // Update invoice totals
      this.invoice.total_excluding_tax = net_amount.toFixed(2);
      this.invoice.total_discount_value = disc_amount.toFixed(2);
      this.invoice.total_tax_amount = tax_amount.toFixed(2);
      this.invoice.total_net_amount = (net_amount).toFixed(2); // Already includes discounts
      this.invoice.total_including_tax = (net_amount + tax_amount).toFixed(2);
      this.invoice.total_payable_amount = (net_amount + tax_amount + rounding_amount - prepayment_amount).toFixed(2);

      // Set tax exemption reason to "NOT APPLICABLE" if tax rate is 0
      if (this.invoice.tax_rate === 0) {
        this.invoice.tax_exemption_reason = "NOT APPLICABLE";
      }
    },

    setCurrentTime() {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const now = new Date();
      const options = {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: userTimezone,
      };

      const timeString = now.toLocaleTimeString('en-GB', options); // Returns "HH:mm:ss" format
      this.invoice.invoice_time = timeString;
    },
    formatDate(date) {
      const d = new Date(date);
      let month = '' + (d.getMonth() + 1);
      let day = '' + d.getDate();
      const year = d.getFullYear();

      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;

      return [year, month, day].join('-');
    }
  }
};
</script>

<style lang="scss">
.select, .select select {
  width: 100%;
}
.form-container {
  width: 1200px;
  justify-content: center;
  align-items: center;
}
.switch {
  display: flex;
  align-items: center;
  cursor: pointer;
}
.switch input {
  display: none;
}
.switch .check {
  width: 40px;
  height: 20px;
  background: #ddd;
  border-radius: 20px;
  position: relative;
  transition: 0.3s;
}
.switch .check::after {
  content: '';
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  position: absolute;
  top: 0;
  left: 0;
  transition: 0.3s;
}
.switch input:checked + .check {
  background: #48c774;
}
.switch input:checked + .check::after {
  left: 20px;
}
.switch .control-label {
  margin-left: 10px;
}
</style>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;   /* Full viewport width */
  height: 100vh;  /* Full viewport height */
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
}

.loading-text {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #110a7a; /* Adjust color as needed */
}
</style>

